import React, { useState, useEffect } from 'react';
import SalesDetailPage from './SalesDetailPage';
import { DEPOSITS_COLUMNS, SORT_TYPES, getDefaultVisibleColumns, getDefaultHiddenColumns } from '../constants/depositsColumnsConfig';
import { currencyFormatter, compactCurrency } from '../utils/reportingUtils';
import { buildDepositsFilterGroups } from '../constants/depositsFiltersConfig';

const axios = require('axios').default;

const DEPOSITS_CONFIG = {
  pageTitle: 'Deposits',
  pageSubtitle: 'Deposit tracking across your venues.',
  sidebarKey: 'finances_deposits',
  defaultPreset: 'year_to_date',
  dateModeOptions: [
    { key: 'event_date', label: 'Event Date' },
  ],

  columns: DEPOSITS_COLUMNS,
  sortTypes: SORT_TYPES,
  getDefaultVisibleColumns,
  getDefaultHiddenColumns,
  localStorageKey: (teamId) => `reportingFinancesDepositColumns_${teamId}`,
  firstColumnId: 'event_name',
  defaultSortRules: [{ field: 'event_date', direction: 'desc' }],

  kpiFromDetailResponse: false,
  salesDataKey: null,
  venueSeriesKey: null,
  exportBasePath: (teamId) => `/teams/${teamId}/reporting/finances/deposits/export`,
  detailEndpoint: (teamId) => `/teams/${teamId}/reporting/finances/deposits/data.json`,
  detailResponseKey: 'rows',
  detailErrorMessage: 'Failed to load deposits data.',
  detailTableTitle: 'Deposits',
  emptyMessage: 'No deposits found.',
  searchPlaceholder: 'Search deposits...',
  paginationLabel: 'deposits',
  itemLabel: (count) => `deposit${count !== 1 ? 's' : ''}`,

  buildKpiCards: () => [],
  kpiGridColumns: 0,

  chartColor: 'var(--sl-text-success, #2e7d32)',
  salesCardIsCurrency: true,
  salesCardTitle: 'Deposits',
  buildMetricValue: () => '',
  buildMetricLabel: () => '',
  overTimeKey: null,
  countLabel: 'deposits',

  topCharts: [],

  groupByOptions: [],
  groupByDefault: null,

  renderCellValue: (col, row, { formatEventDate, currencyFormatter: cf }) => {
    switch (col.id) {
      case 'event_id': return row.event_id || '\u2014';
      case 'event_name': return row.event_name || '\u2014';
      case 'event_status': {
        const status = row.event_status;
        if (!status) return '\u2014';
        return status.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
      }
      case 'venue_name': return row.venue_name || '\u2014';
      case 'event_date': return <span className="text-nowrap">{row.event_date ? formatEventDate(row.event_date) : '\u2014'}</span>;
      case 'due_date': return <span className="text-nowrap">{row.due_date ? formatEventDate(row.due_date) : '\u2014'}</span>;
      case 'payment_status':
        return row.payment_status === 'Paid'
          ? <span className="badge" style={{ background: 'var(--sl-bg-success-primary, #ecfdf3)', color: 'var(--sl-text-success, #027a48)', fontSize: 12 }}>Paid</span>
          : <span className="badge" style={{ background: 'var(--sl-bg-error-primary, #fef3f2)', color: 'var(--sl-text-error, #b42318)', fontSize: 12 }}>Unpaid</span>;
      case 'deposit_due': return cf.format(row.deposit_due || 0);
      case 'artist_guarantee': return cf.format(row.artist_guarantee || 0);
      default: return '\u2014';
    }
  },

  filterTitle: 'Filter Deposits',
  filterSubtitle: 'Apply filters to narrow deposit data.',
};

const buildDetailApiFilters = (activeFilters) => {
  const params = {};
  if (activeFilters.venue_name && activeFilters.venue_name.length > 0) {
    params.venue_name = activeFilters.venue_name;
  }
  if (activeFilters.event_name && activeFilters.event_name.length > 0) {
    params.event_name = activeFilters.event_name;
  }
  if (activeFilters.event_status && activeFilters.event_status.length > 0) {
    params.event_status = activeFilters.event_status;
  }
  if (activeFilters.payment_status && activeFilters.payment_status.length > 0) {
    params.payment_status = activeFilters.payment_status;
  }
  return params;
};

function ReportingFinancesDepositsPage({ team, csrfToken, ...props }) {
  const [filterOptions, setFilterOptions] = useState({});

  useEffect(() => {
    if (!team || !team.id) return;
    if (csrfToken) axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
    axios.get(`/teams/${team.id}/reporting/finances/deposits/filter_options.json`)
      .then((res) => setFilterOptions(res.data || {}))
      .catch(() => {});
  }, [team && team.id, csrfToken]);

  const configWithFilters = {
    ...DEPOSITS_CONFIG,
    buildFilterGroups: () => buildDepositsFilterGroups(filterOptions),
    buildDetailApiFilters,
  };
  return <SalesDetailPage {...props} team={team} csrfToken={csrfToken} config={configWithFilters} />;
}

export default ReportingFinancesDepositsPage;
